import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ChildEdition } from '@/contracts/family';
import { getStory } from '@/content/stories';
import { EditionService, WORLD_SLOTS, WorldService, slotKey } from '@/services/editions';
import { ArtworkService } from '@/services/artwork';
import { useSession } from '@/services/session';
import { EditionReader } from '@/features/editions/EditionReader';
import { Button } from '@/design-system/Button';
import { Card, Chip, EmptyState, SectionHeading } from '@/design-system/Surfaces';
import { SparkleGlyph } from '@/design-system/Sparkle';
import { cn } from '@/design-system/cn';
import { Scene } from '@/content/scenes';
import { IconBack } from '@/features/studio/Icons';
import { useChild } from './childContext';

/** Loads every finished revision for a child, with object URLs for display. */
async function loadArt(account: string, childId: string) {
  const artworks = await ArtworkService.listForChild(account, childId);
  const urls: Record<string, string> = {};
  const revisions = [];
  for (const a of artworks) {
    if (!a.finishedRevisionId) continue;
    const rev = await ArtworkService.getRevision(account, a.finishedRevisionId);
    if (!rev) continue;
    const url = await ArtworkService.previewUrl(account, rev);
    if (url) urls[rev.id] = url;
    revisions.push({ artwork: a, revision: rev, url });
  }
  return { revisions, urls };
}

/* ------------------------------------------------------------------ *
 * C11 — My Edition reader
 * ------------------------------------------------------------------ */

export function EditionReaderScreen() {
  const child = useChild();
  const account = useSession((s) => s.accountId);
  const { editionId } = useParams();
  const navigate = useNavigate();
  const [edition, setEdition] = useState<ChildEdition | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      if (!editionId) return;
      const e = await EditionService.get(account, editionId);
      // An edition belonging to another child never opens (spec §3).
      if (!e || e.childId !== child.id) return;
      setEdition(e);
      const { urls } = await loadArt(account, child.id);
      setUrls(urls);
    })();
  }, [account, child.id, editionId]);

  const story = edition ? getStory(edition.storyId) : null;
  if (!edition || !story) return null;

  return (
    <EditionReader
      story={story}
      edition={edition}
      nickname={child.nickname}
      artUrls={urls}
      coverUrl={edition.coverRevisionId ? (urls[edition.coverRevisionId] ?? null) : null}
      onExit={() => navigate(`/app/child/${child.id}/my-art`)}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Edition builder — reached from My Art / the saved-art screen
 * ------------------------------------------------------------------ */

export function EditionBuilder() {
  const child = useChild();
  const account = useSession((s) => s.accountId);
  const { storyId } = useParams();
  const navigate = useNavigate();
  const story = getStory(storyId ?? '');
  const [edition, setEdition] = useState<ChildEdition | null>(null);
  const [art, setArt] = useState<Awaited<ReturnType<typeof loadArt>>['revisions']>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!story) return;
    const e = await EditionService.ensureForStory(account, child.id, story);
    setEdition(e);
    const loaded = await loadArt(account, child.id);
    setArt(loaded.revisions);
    setUrls(loaded.urls);
  }, [account, child.id, story]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!story) return null;
  if (!edition) return null;

  const slots = story.currentVersion.editionSlots;
  const filled = Object.keys(edition.slots).length;

  const place = async (pageId: string, revisionId: string) => {
    const entry = art.find((a) => a.revision.id === revisionId);
    if (!entry) return;
    const result = await EditionService.placeInSlot(account, edition, story, pageId, entry.revision);
    if ('error' in result) {
      setMessage('That picture does not belong on this page.');
      return;
    }
    setEdition(result);
    setMessage(null);
  };

  return (
    <div className="u-shell py-xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} icon={<IconBack />}>
        Back
      </Button>

      <SectionHeading
        className="mt-l"
        eyebrow="My Edition"
        title={`Your copy of ${story.title}`}
        description="Choose which of your pictures goes on each page. The original book stays exactly as it was — this copy is just yours."
      />

      {message ? (
        <p role="alert" className="mt-l rounded-pebble border-2 border-secondary/40 bg-secondary-soft p-l text-[15px]">
          {message}
        </p>
      ) : null}

      {art.length === 0 ? (
        <div className="mt-xl">
          <EmptyState
            title="No finished pictures yet"
            body="Finish a picture and it can go straight into your own copy of the book."
            action={
              <Button variant="magic" size="lg" to={`/app/child/${child.id}/create/${story.id}`}>
                Make a picture
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="mt-xl space-y-l">
            {slots.map((slot) => {
              const page = story.currentVersion.pages.find((p) => p.id === slot.pageId)!;
              const chosenId = edition.slots[slot.pageId];
              const options = art.filter(
                (a) => a.revision.templateId && slot.acceptsTemplateIds.includes(a.revision.templateId),
              );

              return (
                <Card key={slot.pageId}>
                  <div className="flex flex-col gap-l sm:flex-row">
                    <div className="sm:w-52 shrink-0">
                      <div className="overflow-hidden rounded-pebble border-2 border-line">
                        {chosenId && urls[chosenId] ? (
                          <div className="grid aspect-[3/2] place-items-center bg-white p-s">
                            <img src={urls[chosenId]} alt="" className="max-h-full w-auto" />
                          </div>
                        ) : (
                          <Scene id={page.sceneId} className="aspect-[3/2] w-full" />
                        )}
                      </div>
                      <p className="mt-xs font-display text-[13px] font-semibold text-ink-faint">
                        Page {page.sequence + 1}
                      </p>
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="font-story text-[17px] text-ink">{page.text}</p>

                      {options.length === 0 ? (
                        <p className="mt-m text-[15px] text-ink-faint">
                          Finish “{story.currentVersion.templates.find((t) => slot.acceptsTemplateIds.includes(t.id))?.title}”
                          and it can go here.
                        </p>
                      ) : (
                        <div className="mt-m flex flex-wrap gap-s">
                          {options.map((o) => {
                            const active = chosenId === o.revision.id;
                            return (
                              <button
                                key={o.revision.id}
                                onClick={() => place(slot.pageId, o.revision.id)}
                                aria-pressed={active}
                                className={cn(
                                  'overflow-hidden rounded-pebble border-[3px] bg-white p-xs transition',
                                  active ? 'border-magic shadow-cut' : 'border-line hover:-translate-y-1',
                                )}
                              >
                                {o.url ? (
                                  <img src={o.url} alt={o.artwork.title} className="h-16 w-16 object-contain" />
                                ) : null}
                              </button>
                            );
                          })}
                          {chosenId ? (
                            <button
                              onClick={async () =>
                                setEdition(await EditionService.clearSlot(account, edition, slot.pageId))
                              }
                              className="min-h-touch rounded-pebble border-2 border-dashed border-line px-m font-display text-[14px] font-semibold text-ink-soft"
                            >
                              Use the original
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="mt-l">
            <h3 className="font-display text-[18px] font-semibold text-ink">A cover of your own</h3>
            <p className="mt-xs text-[15px] text-ink-soft">Optional — pick a picture for the front.</p>
            <div className="mt-m flex flex-wrap gap-s">
              {art.map((o) => {
                const active = edition.coverRevisionId === o.revision.id;
                return (
                  <button
                    key={o.revision.id}
                    onClick={async () =>
                      setEdition(
                        await EditionService.setCover(account, edition, active ? null : o.revision.id),
                      )
                    }
                    aria-pressed={active}
                    className={cn(
                      'overflow-hidden rounded-pebble border-[3px] bg-white p-xs transition',
                      active ? 'border-magic shadow-cut' : 'border-line hover:-translate-y-1',
                    )}
                  >
                    {o.url ? <img src={o.url} alt={o.artwork.title} className="h-16 w-16 object-contain" /> : null}
                  </button>
                );
              })}
            </div>
          </Card>

          <div className="mt-2xl flex flex-wrap gap-m">
            <Button
              variant="magic"
              size="xl"
              to={`/app/child/${child.id}/editions/${edition.id}`}
              icon={<SparkleGlyph />}
            >
              Read my copy
            </Button>
            <Chip tone={filled > 0 ? 'leaf' : 'neutral'} className="self-center">
              {filled === 0 ? 'No pictures added yet' : `${filled} of your pictures inside`}
            </Chip>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * C13 — My World placement editor
 * ------------------------------------------------------------------ */

export function WorldEditor() {
  const child = useChild();
  const account = useSession((s) => s.accountId);
  const navigate = useNavigate();
  const [placements, setPlacements] = useState<Awaited<ReturnType<typeof WorldService.listForChild>>>([]);
  const [art, setArt] = useState<Awaited<ReturnType<typeof loadArt>>['revisions']>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [activeSlot, setActiveSlot] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setPlacements(await WorldService.listForChild(account, child.id));
    const loaded = await loadArt(account, child.id);
    setArt(loaded.revisions);
    setUrls(loaded.urls);
  }, [account, child.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const byKey = new Map(placements.map((p) => [slotKey(p.slot, p.slotIndex), p]));
  const slotDef = WORLD_SLOTS.find((s) => slotKey(s.kind, s.index) === activeSlot) ?? null;

  const eligibleFor = (kind: string) =>
    art.filter((a) => {
      const tpl = a.artwork.templateId;
      if (!tpl) return false;
      const story = getStory(a.artwork.storyId ?? '');
      const t = story?.currentVersion.templates.find((x) => x.id === tpl);
      return t?.worldSlots.includes(kind as never) ?? false;
    });

  return (
    <div className="u-shell py-xl">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} icon={<IconBack />}>
        Back
      </Button>

      <SectionHeading
        className="mt-l"
        eyebrow="My World"
        title="Put your pictures up"
        description="Tap a spot, then choose something you made. Taking a picture down never deletes it."
      />

      {message ? (
        <p role="alert" className="mt-l rounded-pebble border-2 border-secondary/40 bg-secondary-soft p-l text-[15px]">
          {message}
        </p>
      ) : null}

      <div className="mt-xl overflow-hidden rounded-blob border-[3px] border-line shadow-cut-lg">
        <div className="relative">
          <Scene
            id="world-room"
            fit="contain"
            className="aspect-[3/2] w-full"
            title={`${child.nickname}'s world`}
          />
          {WORLD_SLOTS.map((slot) => {
            const key = slotKey(slot.kind, slot.index);
            const placed = byKey.get(key);
            const url = placed ? urls[placed.artworkRevisionId] : null;
            const selected = activeSlot === key;
            return (
              <button
                key={key}
                onClick={() => setActiveSlot(selected ? null : key)}
                aria-label={placed ? `${slot.label} — change what is here` : `${slot.label} — empty`}
                aria-pressed={selected}
                className={cn(
                  'absolute grid place-items-center overflow-hidden rounded-md border-[3px] transition',
                  selected
                    ? 'border-magic bg-magic/15 shadow-cut'
                    : placed
                      ? 'border-white/80 bg-white/10'
                      : 'border-dashed border-white/70 bg-black/15 hover:bg-black/25',
                )}
                style={{ left: `${slot.x}%`, top: `${slot.y}%`, width: `${slot.w}%`, height: `${slot.h}%` }}
              >
                {url ? (
                  // Absolutely positioned so the height is definite — a
                  // centred grid item resolves `height:100%` to auto and the
                  // picture would spill out of its frame.
                  <img
                    src={url}
                    alt=""
                    className="absolute inset-[3px] h-auto max-h-[calc(100%-6px)] w-auto max-w-[calc(100%-6px)] rounded-sm bg-white object-contain"
                    style={{ margin: 'auto' }}
                  />
                ) : (
                  <span className="font-display text-[11px] font-semibold text-white/90">+</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {slotDef ? (
        <Card className="mt-l animate-slide-up">
          <div className="flex flex-wrap items-center justify-between gap-m">
            <h3 className="font-display text-[18px] font-semibold text-ink">{slotDef.label}</h3>
            {byKey.get(activeSlot!) ? (
              <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                  await WorldService.remove(account, byKey.get(activeSlot!)!.id);
                  await refresh();
                }}
              >
                Take it down
              </Button>
            ) : null}
          </div>

          {eligibleFor(slotDef.kind).length === 0 ? (
            <p className="mt-m text-[15px] text-ink-soft">
              Nothing you have made fits here yet. Finish a picture and come back.
            </p>
          ) : (
            <div className="mt-m flex flex-wrap gap-s">
              {eligibleFor(slotDef.kind).map((o) => (
                <button
                  key={o.revision.id}
                  onClick={async () => {
                    const story = getStory(o.artwork.storyId ?? '');
                    const tpl = story?.currentVersion.templates.find((t) => t.id === o.artwork.templateId);
                    const res = await WorldService.place(
                      account, child.id, slotDef, o.revision, tpl?.worldSlots ?? [],
                    );
                    if ('error' in res) setMessage('That one does not fit in this spot.');
                    else {
                      setMessage(null);
                      await refresh();
                    }
                  }}
                  className="overflow-hidden rounded-pebble border-[3px] border-line bg-white p-xs transition hover:-translate-y-1"
                >
                  {o.url ? <img src={o.url} alt={o.artwork.title} className="h-16 w-16 object-contain" /> : null}
                </button>
              ))}
            </div>
          )}
          <p className="mt-m text-[14px] text-ink-faint">
            Taking a picture down only clears this spot. The picture stays in My Art.
          </p>
        </Card>
      ) : (
        <p className="mt-l text-[15px] text-ink-soft">Tap a spot in the room to put something there.</p>
      )}
    </div>
  );
}
